import { useLayoutEffect, useRef } from "react"
import { ArrowUp } from "lucide-react"
import {
  LIVE_PROOF_CHAT_HEADER,
  LIVE_PROOF_CHAT_HISTORY,
  LIVE_PROOF_HEADLINE,
  LIVE_PROOF_LATENCY_BADGE_LIMIT,
  LIVE_PROOF_PRIVACY_NOTE,
  LIVE_PROOF_STATS,
  LIVE_PROOF_STAMP,
  LIVE_PROOF_SUBHEAD,
  LIVE_PROOF_TAG,
  LIVE_PROOF_TIMELINE,
} from "../../main/liveProof/liveProofContent"

const headingClass =
  "text-balance text-[40px] font-bold leading-[48px] tracking-[-0.04em] text-[#171717] max-md:text-[28px] max-md:leading-8"

const cardShadow =
  "shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_2px_2px_0_rgba(0,0,0,0.04),0_8px_16px_-4px_rgba(0,0,0,0.04),0_0_0_1px_#FAFAFA]"

const bubbleShadow =
  "shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_2px_2px_0_rgba(0,0,0,0.04),0_0_0_1px_#FAFAFA]"

const windowDots = ["#EE6D5E", "#F3BF4A", "#5DC753"]

function AssistantMark() {
  return (
    <span
      className={`flex size-7 shrink-0 items-center justify-center rounded-full bg-white text-[#171717] ${bubbleShadow}`}
    >
      <svg viewBox="0 0 16 16" className="size-3.5" aria-hidden="true">
        <path
          fill="currentColor"
          fillRule="evenodd"
          d="M2.5.5V0h1v.5c0 1.1.9 2 2 2H6v1h-.5a2 2 0 0 0-2 2V6h-1v-.5a2 2 0 0 0-2-2H0v-1h.5a2 2 0 0 0 2-2m12 4V5h-1v-.5a1 1 0 0 0-1-1H12v-1h.5a1 1 0 0 0 1-1V1h1v.5a1 1 0 0 0 1 1h.5v1h-.5a1 1 0 0 0-1 1m-6-.5-.1.93A5 5 0 0 1 3.94 9.4L3 9.5v1l.93.1a5 5 0 0 1 4.48 4.47l.09.93h1l.1-.93a5 5 0 0 1 4.47-4.48l.93-.09v-1l-.93-.1A5 5 0 0 1 9.6 4.94L9.5 4z"
          clipRule="evenodd"
        />
      </svg>
    </span>
  )
}

function BubbleTail({ side }) {
  const sideClass = side === "right" ? "right-[-7px] -scale-x-100" : "left-[-7px]"

  return (
    <svg
      aria-hidden="true"
      className={`pointer-events-none absolute bottom-[-3px] ${sideClass}`}
      fill="none"
      height="24"
      width="24"
      viewBox="0 0 24 24"
    >
      <path d="M0 0H24V24H0z" fill="white" />
      <path
        clipRule="evenodd"
        d="M27-19C15.954-19 7-10.046 7 1c0 .335.008.669.025 1H7v10a15 15 0 01-3 9c4.116 0 7.845-1.658 10.555-4.342A19.915 19.915 0 0027 21c11.046 0 20-8.954 20-20s-8.954-20-20-20z"
        fill="white"
        fillRule="evenodd"
        stroke="rgba(0,0,0,0.08)"
        strokeWidth="2"
      />
    </svg>
  )
}

export default function LiveProofSection() {
  const transcriptRef = useRef(null)
  let assistantCount = 0
  const headline = `${LIVE_PROOF_HEADLINE.lead} ${LIVE_PROOF_HEADLINE.accentPrimary} ${LIVE_PROOF_HEADLINE.leadMid} ${LIVE_PROOF_HEADLINE.accentSecondary}`

  useLayoutEffect(() => {
    const node = transcriptRef.current
    if (!node) return
    node.scrollTop = node.scrollHeight
  }, [])

  return (
    <section className="ml-[calc(50vw-50%-1.5rem)] flex w-full flex-col items-start gap-8">
      <div className="flex flex-col gap-3">
        <p className="text-[14px] font-medium leading-5 text-[#4D4D4D]">
          {`${LIVE_PROOF_STAMP} • ${LIVE_PROOF_TAG}`}
        </p>
        <h2 className={headingClass}>{headline}</h2>
      </div>
      <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-3">
        {LIVE_PROOF_STATS.map((stat) => (
          <article
            key={stat.label}
            className="flex flex-col gap-2 rounded-[2px] border border-solid border-[#E6E6E6] bg-white p-6"
          >
            <p className="text-balance text-[24px] font-bold leading-8 tracking-[-0.04em] text-[#171717]">
              {stat.value}
            </p>
            <p className="text-[13px] font-light leading-[18px] text-[#171717]">{stat.label}</p>
            <p className="text-[16px] font-normal leading-6 text-[#4D4D4D]">{stat.sub}</p>
          </article>
        ))}
      </div>
      <p className="max-w-[68ch] text-[18px] font-normal leading-7 text-[#4D4D4D]">
        {LIVE_PROOF_SUBHEAD}
      </p>
      <div
        className={`flex max-h-[95vh] w-full flex-col overflow-hidden rounded-xl bg-white ${cardShadow}`}
      >
        <div className="relative flex h-11 shrink-0 items-center justify-between px-3 shadow-[inset_0_-1px_0_rgba(0,0,0,0.08)]">
          <div className="flex min-w-[62px] items-center gap-1 pl-0">
            {windowDots.map((color) => (
              <span
                key={color}
                className="size-2 rounded-full"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          <p className="pointer-events-none absolute left-1/2 -translate-x-1/2 text-[13px] font-normal leading-4 text-[#7D7D7D] max-md:static max-md:translate-x-0">
            {LIVE_PROOF_CHAT_HEADER.label}
          </p>
          <p className="text-[13px] font-medium leading-4 text-[#171717]">
            {`✓ ${LIVE_PROOF_CHAT_HEADER.status}`}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-end gap-6 px-6 py-4 shadow-[inset_0_-1px_0_rgba(0,0,0,0.08)]">
          {LIVE_PROOF_TIMELINE.map((step) => (
            <div key={step.label} className="flex flex-col gap-1">
              <p className="text-[14px] font-medium leading-5 text-[#171717]">{step.time}</p>
              <p className="text-[13px] font-light leading-[18px] text-[#171717]">{step.label}</p>
            </div>
          ))}
          <p className="max-w-[56ch] text-[14px] font-normal leading-5 text-[#4D4D4D] sm:ml-auto sm:text-right">
            {LIVE_PROOF_PRIVACY_NOTE}
          </p>
        </div>
        <div
          ref={transcriptRef}
          className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain px-6 pb-3 pt-6"
        >
          {LIVE_PROOF_CHAT_HISTORY.map((message, index) => {
            const isUser = message.role === "user"
            let latency = null
            if (!isUser) {
              assistantCount += 1
              if (
                message.response_time_seconds != null &&
                assistantCount <= LIVE_PROOF_LATENCY_BADGE_LIMIT
              ) {
                latency = `Replied in ${message.response_time_seconds}s`
              }
            }
            const key = message.message_id ?? message.timestamp ?? index

            return (
              <div key={key} className={`flex ${isUser ? "justify-end" : "items-end gap-2.5"}`}>
                {isUser ? null : <AssistantMark />}
                <div className={`flex max-w-[85%] flex-col gap-1 ${isUser ? "items-end" : ""}`}>
                  <div
                    className={`relative rounded-[18px] bg-white px-4 py-2.5 text-[14px] leading-[22px] text-[#171717] ${bubbleShadow}`}
                  >
                    <BubbleTail side={isUser ? "right" : "left"} />
                    <p
                      className={`relative whitespace-pre-wrap ${
                        message.highlight ? "font-semibold" : "font-normal"
                      }`}
                    >
                      {message.content}
                    </p>
                  </div>
                  {latency ? (
                    <p className="text-[13px] font-medium leading-5 text-[#4D4D4D]">{latency}</p>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
        <div className="shrink-0 px-6 pb-6">
          <div
            aria-hidden="true"
            className="flex items-center gap-2 rounded-[6px] border border-solid border-black/[0.08] bg-[#FAFAFA] py-1.5 pl-3 pr-1.5"
          >
            <span className="min-w-0 flex-1 truncate text-[12px] leading-4 text-[#4D4D4D]">
              Ask a question…
            </span>
            <span className="flex size-6 shrink-0 items-center justify-center rounded-[4px] bg-[#171717] text-white">
              <ArrowUp className="size-3" strokeWidth={2.5} />
            </span>
          </div>
        </div>
      </div>
    </section>
  )
}
